import { createDraftSafeSelector } from '@reduxjs/toolkit';

import type { RootState } from '@polyai/stream/store';
import {
  attributeSelectors,
  variantAttributeValuesSelectors,
  variantSelectors,
} from '@polyai/stream/store/reducers/variant/index';

const selectRootVariantManagement = (state: RootState) =>
  state.variantManagement;

export const selectVariantManagement = createDraftSafeSelector(
  [selectRootVariantManagement],
  ({ variants, attributes, variantAttributeValues }) => ({
    variants,
    attributes,
    variantAttributeValues,
  }),
);

export const selectVariant = createDraftSafeSelector(
  [selectRootVariantManagement],
  ({ variants }) => {
    return variantSelectors.selectAll(variants);
  },
);

export const selectAttribute = createDraftSafeSelector(
  [selectRootVariantManagement],
  ({ attributes }) => {
    return attributeSelectors.selectAll(attributes);
  },
);

export const selectActiveAttributes = createDraftSafeSelector(
  [selectRootVariantManagement],
  ({ attributes }) => {
    return attributeSelectors.selectAll(attributes).filter(a => !a.archived);
  },
);

export const selectAttributes = createDraftSafeSelector(
  [selectVariantManagement],
  ({ attributes }) => {
    return attributeSelectors.selectAll(attributes);
  },
);

export const selectVariants = createDraftSafeSelector(
  [selectVariantManagement],
  ({ variants }) => {
    return variantSelectors.selectAll(variants);
  },
);

export const selectVariantAttributeValues = createDraftSafeSelector(
  [selectRootVariantManagement],
  ({ variantAttributeValues }) => {
    return variantAttributeValuesSelectors.selectAll(variantAttributeValues);
  },
);

export const selectVariantByIds = createDraftSafeSelector(
  [selectVariantManagement, (_state: RootState, ids: string[]) => ids],
  ({ variants }, ids) => {
    return ids.map((id: string) => {
      return variantSelectors.selectById(variants, id);
    });
  },
);

export const selectVariantById = createDraftSafeSelector(
  [selectVariantManagement, (_state: RootState, id: string) => id],
  ({ variants }, id) => {
    return variantSelectors.selectById(variants, id);
  },
);

export const selectAttributeByIds = createDraftSafeSelector(
  [selectVariantManagement, (_state: RootState, ids: string[]) => ids],
  ({ attributes }, ids) => {
    return ids.map((id: string) => {
      return attributeSelectors.selectById(attributes, id);
    });
  },
);

export const selectAttributeById = createDraftSafeSelector(
  [selectVariantManagement, (_state: RootState, id: string) => id],
  ({ attributes }, id) => {
    return attributeSelectors.selectById(attributes, id);
  },
);
